<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import authService from '@/core/auth/auth.service';
import sitePickerOverlayService, { SHOW_SITE_PICKER } from '@/project/modals/overlays/SitePickerOverlay.service';
import siteService from './site.service';
import botService from './bot.service';
import Api from '@/project/http/Api.service';
import { SwitchSiteResponseViewModel } from '@/types/apiServerContract';
import bus from '@/core/bus/index';
import { ModalState } from '@/core/modals/ModalState';

export default defineComponent({
    setup(props, { slots }) {

        const updatingServer = ref<boolean>(false);
        const cookieScriptRetryCounter = ref<number>(0);

        const modals = computed(() => {
            return ModalState.modals;
        });

        const isLoggedIn = computed(() => {
            return authService.isLoggedIn();
        });

        function validateIfSitePickerShouldShow() {
            const isLoggedIn = authService.isLoggedIn();
            const isBot = (botService.uaIsPrerender() || botService.uaIsBot());
            const isOnFrontpage = document.location.pathname === window.servercontext.url;
            const isModalActive = modals.value.length > 0;
            if (!isLoggedIn && !isBot && !siteService.hasBeenPickedByUser() && !isModalActive && isOnFrontpage) {
                // Don't show sitePicker if there is also a cookiebot.com banner shown
                if (hasUserGivenCookieConsent()) {
                    setTimeout(showSitePicker, 1000);
                    return;
                } else if (!window.Cookiebot?.consent || cookieScriptRetryCounter.value > 4) {
                    // Cookie consent script is not load, yet. It might fail or timeout
                    // this.cookieScriptRetryCounter: retry counter if cookiebot script loaded slow, wait in max 5 seconds
                    cookieScriptRetryCounter.value++;
                    showSitePicker();
                } else if (!hasUserGivenCookieConsent()) {
                    // script is loaded and user is reading cookie banner
                    setTimeout(validateIfSitePickerShouldShow, 1000);
                }
            }
        }

        function hasUserGivenCookieConsent(): boolean {
            return !!window.Cookiebot?.hasResponse;
        }

        async function showSitePicker() {
            const currentSiteId = window.servercontext.siteId;
            const selectedSite: any = await sitePickerOverlayService.show();
            const selectedSiteId = selectedSite?.siteId;
            if (selectedSite && currentSiteId !== selectedSiteId) {
                await submit(selectedSiteId);
            }
        }

        async function submit(siteId) {
            if (updatingServer.value) { return; }

            try {
                updatingServer.value = true;
                const result: SwitchSiteResponseViewModel = await Api.sites.switchSite({ siteId }, 'switch-site');

                if (result.token) {
                    authService.setToken(result.token);
                }
                siteService.update(result);
            } catch (err) {
                // Ok, validation-messages will be shown
            } finally {
                updatingServer.value = false;
            }
        }

        watch(() => isLoggedIn.value, () => {
            loadSiteOptions();
        });

        async function loadSiteOptions() {
            const siteOptions = await Api.sites.siteOptions();
            siteService.setCountryOptions(siteOptions);

            const siteHasMultipleSiteOptions = siteOptions?.countries?.length && (siteOptions?.countries.length > 1 || siteOptions.countries[0].sites.length > 1);
            if (siteHasMultipleSiteOptions) {
                validateIfSitePickerShouldShow();
            }
        }

        onMounted(() => {
            loadSiteOptions();
            bus.on(SHOW_SITE_PICKER, showSitePicker);
        });

        onBeforeUnmount(() => {
            bus.off(SHOW_SITE_PICKER, showSitePicker);
        });

        return () => {
            return slots.default?.();
        };
    },
});
</script>
